// noinspection ES6UnusedImports
import SliderLandmarkHelper from '@core/scripts/components/tracking/landmarks/slider-landmark-helper';
import Messages from '@core/scripts/components/messages';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import RecommendationAPI from '@personalization/scripts/apis/recommendation-api';
import SliderRecommendationItems from '@personalization/scripts/components/SliderRecommendationItems.jsx';
import React, { useEffect, useRef, useState } from 'react';
import Slider, { SliderAction } from './Slider.jsx';
import Logger from "@core/scripts/components/logger.js";

let observer;

export default function RecommendationSlider({
    swiperDataUrl,
    paramStringWithPageViewId,
    className = 'recommendations',
    lazyloadingPixelDistance,
    onLoaded,
    imagesOnly,
    loadingEnabledRef,
    recommendationType,
    sliderType,
    onLinkClicked,
    optimizeButtonHandling,
    immediateLoading,
    children,
}) {
    const [recoData, setRecoData] = useState(null);

    const loadedDataForParamStrings = useRef({});
    const activeItemNumber = useRef(null);
    const inViewRef = useRef(null);

    useEffect(() => {
        const subscription = MessageBroker.subscribe(
            Messages.RECOMMENDATIONS.ITEM_CHANGED,
            ({itemNumber, types}) => {
                if (itemNumber && types?.includes(sliderType)) {
                    const urlSearchParams = new URLSearchParams(paramStringWithPageViewId);
                    urlSearchParams.set('itemNumber', itemNumber);
                    paramStringWithPageViewId = '?' + urlSearchParams.toString();

                    if (lazyloadingPixelDistance && inViewRef?.current) {
                        observer.observe(inViewRef.current);
                    } else {
                        loadContentAndFillModel();
                    }
                }
            }
        );

        return () => MessageBroker.unsubscribe(subscription);
    }, []);

    useEffect(() => {
        if (lazyloadingPixelDistance) {
            if (inViewRef.current) {
                observer = new IntersectionObserver(
                    ([entry]) => {
                        if (loadingEnabledRef) {
                            // See SSS-616 for details
                            if (entry.isIntersecting && loadingEnabledRef.current) {
                                loadContentAndFillModel();
                                observer.unobserve(inViewRef.current);
                            }
                        } else if (entry.isIntersecting) {
                            loadContentAndFillModel();
                            observer.unobserve(inViewRef.current);
                        }
                    },
                    {
                        rootMargin: `0px 0px ${lazyloadingPixelDistance}px 0px`,
                    }
                );
                if (inViewRef.current) {
                    observer.observe(inViewRef.current);
                }
            }
        } else {
            if (inViewRef.current) {
                loadContentAndFillModel();
            }
        }
    }, [inViewRef]);

    function loadContentAndFillModel() {
        const dataForParamString = loadedDataForParamStrings.current[paramStringWithPageViewId];
        const urlSearchParams = new URLSearchParams(paramStringWithPageViewId);
        const paramStringItemNumber = urlSearchParams.get('itemNumber');

        if (paramStringItemNumber && paramStringItemNumber === activeItemNumber.current) {
            return;
        }

        if (dataForParamString) {
            onRecommendationsFetched(dataForParamString);
        } else {
            RecommendationAPI.get(swiperDataUrl, paramStringWithPageViewId, 'Fetching Product Recommendations failed')
                .then(onRecommendationsFetched)
                .catch(onFetchingRecommendationsFailed);
        }
    }

    const onFetchingRecommendationsFailed = (error) => {
        Logger.debug(error);
    }

    const onRecommendationsFetched = (data) => {
        if (data?.recommendationProducts) {
            loadedDataForParamStrings.current[paramStringWithPageViewId] = data;
            const urlSearchParams = new URLSearchParams(paramStringWithPageViewId);
            activeItemNumber.current = urlSearchParams.get('itemNumber');
        }
        setRecoData(data);
        onLoaded?.(data, recommendationType, inViewRef.current);
        SliderAction.changeSlideTo({
            sliderId: `reco-${data?.recommendationIdentifier}`,
            slideIndex: 0,
        });
    };

    if (immediateLoading && !recoData) {
        loadContentAndFillModel();
        return <div ref={inViewRef} />;
    }

    if (!recoData) {
        return <div ref={inViewRef} />;
    }

    const {landmarkData, recommendationsTitle, multiplePricesText, recommendationIdentifier, recommendationProducts} =
        recoData;

    const onSlideChanged = () => {
        SliderLandmarkHelper.createSendSlideChangedLandmark(landmarkData);
    };

    const onSlideClicked = (clickedItem) => {
        SliderLandmarkHelper.createAndSendSlideClickedLandmark(sliderType, clickedItem, landmarkData);
    };

    const onLinkClick = (clickedItem) => {
        onLinkClicked?.(recommendationType, clickedItem);
    };

    const onIsVisible = (event) => {
        SliderLandmarkHelper.createAndSendSliderVisibilityLandmark(sliderType, event, landmarkData, false);
    };

    return (
        recommendationProducts?.length > 0 && (
            <div className={className} ref={inViewRef}>
                <Slider
                    id={`reco-${recommendationIdentifier}`}
                    items={recommendationProducts}
                    title={recommendationsTitle}
                    imagesOnly={imagesOnly}
                    onIsVisible={onIsVisible}
                    onSlideChanged={onSlideChanged}
                    optimizeButtonHandling={optimizeButtonHandling}
                >
                    <SliderRecommendationItems
                        className={className}
                        items={recommendationProducts}
                        multiplePricesText={multiplePricesText}
                        imagesOnly={imagesOnly}
                        onSlideClicked={onSlideClicked}
                        onLinkClicked={onLinkClicked ? onLinkClick : null}
                    >
                        {children}
                    </SliderRecommendationItems>
                </Slider>
            </div>
        )
    );
}
